import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ExternalLink, Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
import { SettingsRoutes } from '@/router/routes';

import { MEMBER_ACCESS_FILTER_KEY_GROUP_IDS } from '../../constants/member-access-filter-keys';
import { useDeleteUserGroup } from '../../hooks/user-groups/use-delete-user-group';
import { UserGroupPermissions } from '../../types/permission';
import { isSuperAdminUserGroup } from '../../utils/user-group.utils';
import {
  DeleteUserGroupFormValues,
  deleteUserGroupSchema,
} from '../../validations/user-group-validation';

const FORM_FIELD_REPLACEMENT_GROUP_ID = 'replacementGroupId';

/** Members list pre-filtered to a single group, so the admin can see who the delete will move. */
const buildGroupMembersUrl = (groupId: number): string => {
  const params = new URLSearchParams({ [MEMBER_ACCESS_FILTER_KEY_GROUP_IDS]: String(groupId) });
  return `${SettingsRoutes.Members}?${params.toString()}`;
};

interface DeleteUserGroupModalProps {
  group: UserGroupPermissions | null;
  allGroups: UserGroupPermissions[];
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Confirmation modal for deleting a user group.
 *
 * The replacement group is always required: the backend moves the deleted group's members
 * into it. The API returns no member count, so the copy cannot quantify who is affected —
 * instead the modal links out to the members list filtered to this group (new tab, so the
 * in-progress delete is not lost).
 */
export const DeleteUserGroupModal = ({
  group,
  allGroups,
  open,
  onClose,
  onSuccess,
}: DeleteUserGroupModalProps) => {
  const { t } = useTranslation('authenticated.settings');

  const form = useForm<DeleteUserGroupFormValues>({
    resolver: zodResolver(deleteUserGroupSchema),
    mode: 'onChange',
    defaultValues: { [FORM_FIELD_REPLACEMENT_GROUP_ID]: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset({ [FORM_FIELD_REPLACEMENT_GROUP_ID]: '' });
    }
  }, [open, group, form]);

  const deleteMutation = useDeleteUserGroup({
    onSuccess: () => {
      form.reset();
      onSuccess?.();
    },
  });

  const replacementOptions = allGroups
    .filter(candidate => candidate.id !== group?.id && !isSuperAdminUserGroup(candidate))
    .map(candidate => ({ value: String(candidate.id), label: candidate.name }));

  const replacementGroupId = form.watch(FORM_FIELD_REPLACEMENT_GROUP_ID);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const handleDelete = async (closeModal: () => void) => {
    if (!group) return;

    const isValid = await form.trigger();
    if (!isValid) return;

    try {
      await deleteMutation.mutateAsync({
        id: group.id,
        replacementGroupId: Number(form.getValues().replacementGroupId),
        name: group.name,
      });
      closeModal();
    } catch {
      // Error handled by the mutation's onError
    }
  };

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => {}}
      onClose={handleClose}
      title={t('pages.permissions.deleteGroupModal.title')}
      message={
        <div className="text-sm text-muted-foreground">
          <Trans
            i18nKey="authenticated.settings:pages.permissions.deleteGroupModal.message"
            values={{ name: group?.name }}
            components={{ strong: <strong className="font-semibold text-foreground" /> }}
          />
        </div>
      }
      confirmText={
        deleteMutation.isPending
          ? t('pages.permissions.deleteGroupModal.deleting')
          : t('pages.permissions.deleteGroupModal.delete')
      }
      confirmVariant="destructive"
      onConfirm={handleDelete}
      isLoading={deleteMutation.isPending}
      disabled={!replacementGroupId}
    >
      <div className="space-y-4">
        {/* Static copy on purpose: the alert renders before a replacement is picked, so naming the
            incoming default here would make the text rewrite itself mid-flow. */}
        {group?.isDefault && (
          <Alert variant="info">
            <Info className="h-4 w-4" />
            <AlertDescription>
              {t('pages.permissions.deleteGroupModal.defaultGroupInfo')}
            </AlertDescription>
          </Alert>
        )}

        {group && (
          <Link
            to={buildGroupMembersUrl(group.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary underline-offset-2 hover:underline"
          >
            {t('pages.permissions.deleteGroupModal.viewMembers')}
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        )}

        <Form {...form}>
          <form>
            <SingleSelect
              name={FORM_FIELD_REPLACEMENT_GROUP_ID}
              control={form.control}
              options={replacementOptions}
              label={t('pages.permissions.deleteGroupModal.replacementLabel')}
              placeholder={t('pages.permissions.deleteGroupModal.replacementPlaceholder')}
              searchable
            />
          </form>
        </Form>
      </div>
    </ConfirmationModal>
  );
};
